// The landing page — Direction A "The Dining Room". Hero owns the store's
// identity; mode pills never block browsing (Pickup·ASAP is the default
// session); the category rail tracks scroll; the cart follows as a navy bar
// (mobile) or a docked order rail (desktop). The menu is the server's
// composed document for the channel: what is orderable, at what price, is
// its verdict — the page only decides what to show.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import {
  CHANNEL_LABEL, byRank, cardPrice, closesAt, fetchBookingConfig, fetchHours, fetchMenu, fetchStore, itemsById, money, openNowFor,
  type Channel, type HoursView, type MenuDocument, type MenuItemView, type StorePublic,
} from '../lib/api';
import { cartCount, cartSubtotal, setChannel as setCartChannel, setQuantity, useCart } from '../lib/cart';
import { pinnedTable } from '../lib/dinein';
import { ItemSheet } from './ItemSheet';

export function Storefront() {
  const { rid, sid } = useParams() as { rid: string; sid: string };
  const storeKey = `${rid}/${sid}`;
  const [sp] = useSearchParams();
  const [store, setStore] = useState<StorePublic | null>(null);
  const [hours, setHours] = useState<HoursView | null>(null);
  const [menu, setMenu] = useState<MenuDocument | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [booksOnline, setBooksOnline] = useState(false);
  const [channel, setChannel] = useState<Channel>(sp.get('dinein') || pinnedTable(storeKey) ? 'dine_in' : 'pickup');
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [sheet, setSheet] = useState<MenuItemView | null>(null);
  const cart = useCart(storeKey);
  const catRefs = useRef<Record<string, HTMLElement | null>>({});
  const menuSeq = useRef(0);

  useEffect(() => {
    let live = true;
    Promise.all([fetchStore(rid, sid), fetchHours(rid, sid)]).then(
      ([s, h]) => {
        if (!live) return;
        setStore(s);
        setHours(h);
        if (!s.pickupEnabled) setChannel(s.deliveryEnabled ? 'delivery' : 'dine_in');
        document.title = `${s.name} — Order`;
      },
      (e) => live && setError(e instanceof Error ? e.message : 'Could not load the store.'),
    );
    // the booking book is its own surface — a failed read only hides the entry
    fetchBookingConfig(rid, sid).then((c) => live && setBooksOnline(c.settings.enabled), () => {});
    return () => { live = false; };
  }, [rid, sid]);

  // the composed menu is per channel — a channel switch re-reads it
  useEffect(() => {
    const mine = ++menuSeq.current;
    fetchMenu(rid, sid, channel).then(
      (m) => { if (mine === menuSeq.current) setMenu(m); },
      (e) => { if (mine === menuSeq.current) setError(e instanceof Error ? e.message : 'Could not load the menu.'); },
    );
    setCartChannel(storeKey, channel);
  }, [rid, sid, channel, storeKey]);

  // category rail follows scroll
  useEffect(() => {
    if (!menu) return;
    const obs = new IntersectionObserver(
      (entries) => { for (const e of entries) if (e.isIntersecting) setActiveCat(e.target.getAttribute('data-cat')); },
      { rootMargin: '-120px 0px -70% 0px' },
    );
    Object.values(catRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [menu]);

  const all = useMemo(() => (menu ? itemsById(menu) : new Map<string, MenuItemView>()), [menu]);
  const sections = useMemo(() => {
    if (!menu) return [];
    // sold-out stays visible but quiet; everything else the channel refuses is not shown
    const showable = (i: MenuItemView) => i.orderable || i.unorderableReason === 'sold_out';
    return menu.categories
      .slice()
      .sort(byRank)
      .map((cat) => ({ cat, items: cat.items.filter(showable).sort(byRank) }))
      .filter((s) => s.items.length > 0);
  }, [menu]);

  if (error) return <div className="errbox" role="alert">{error}</div>;
  if (!store || !menu) return <div className="loading">Setting the table…</div>;

  const openNow = openNowFor(hours, channel);
  const closes = closesAt(hours, channel);
  const closed = store.temporarilyClosed || store.permanentlyClosed;
  const channels = (['pickup', 'delivery', 'dine_in'] as Channel[]).filter((c) => (c === 'pickup' ? store.pickupEnabled : c === 'delivery' ? store.deliveryEnabled : store.dineInEnabled));
  const count = cartCount(cart);
  const subtotal = cartSubtotal(cart);
  const table = pinnedTable(storeKey);

  return (
    <>
      {closed && <div className="closed-banner">{store.permanentlyClosed ? 'This store has closed permanently.' : 'Temporarily closed — back soon.'}</div>}
      <header className="hero">
        <div className="veil" />
        {booksOnline && !store.permanentlyClosed && <Link to={`/r/${rid}/s/${sid}/book`} className="hero-book">Book a table</Link>}
        <div className="id">
          <h1 className="name">{store.name}</h1>
          <div className="tagline">{store.address}</div>
          <div className="chips">
            <span className={'chip ' + (openNow && !closed ? 'live' : 'closed')} data-open={openNow && !closed}>
              {closed ? 'Closed' : openNow ? `Open now${closes ? ` · closes ${closes}` : ''}` : 'Closed now — schedule ahead'}
            </span>
            {channels.map((c) => <span key={c} className="chip">{CHANNEL_LABEL[c]}</span>)}
          </div>
        </div>
      </header>

      <nav className="band" aria-label="How you'll get it">
        {channels.map((c) => (
          <button key={c} className={'mode' + (channel === c ? ' on' : '')} aria-pressed={channel === c} onClick={() => setChannel(c)}>
            {CHANNEL_LABEL[c]}
          </button>
        ))}
        <span className="when">
          {table && channel === 'dine_in' ? `Table ${table.tableName}` : 'ASAP · today'}
          {' · '}
          <Link to={`/r/${rid}/s/${sid}/account`} className="linky">Account</Link>
        </span>
      </nav>

      <nav className="cats" aria-label="Categories">
        {sections.map(({ cat }) => (
          <a key={cat.categoryId} href={`#cat-${cat.categoryId}`} className={activeCat === cat.categoryId ? 'on' : ''}>{cat.name}</a>
        ))}
      </nav>

      <div className="wrap">
        <div className="columns">
          <main className="menu">
            {sections.length === 0 && <div className="loading">The menu is being written — check back soon.</div>}
            {sections.map(({ cat, items }) => (
              <section key={cat.categoryId} id={`cat-${cat.categoryId}`} className="mcat" data-cat={cat.categoryId} ref={(el) => { catRefs.current[cat.categoryId] = el; }}>
                <h2>{cat.name}</h2>
                {cat.description && <div className="cdesc">{cat.description}</div>}
                {items.map((item) => {
                  const p = cardPrice(item, menu, all);
                  const blocked = !item.orderable;
                  return (
                    <button key={item.itemId} className={'dish' + (blocked ? ' blocked' : '')} data-item={item.itemId} aria-label={item.name} disabled={blocked} onClick={() => setSheet(item)}>
                      <span className="tx">
                        <span className="nm">{item.name}</span>
                        {item.description && <span className="ds">{item.description}</span>}
                        <span className="pr">
                          {item.soldOut && <span className="soldtag">Sold out today · </span>}
                          {p && (<>{p.from && <span className="from">from </span>}{money(p.cents)}</>)}
                        </span>
                      </span>
                      {item.imageUrl ? (
                        <span className="imwrap"><img src={item.imageUrl} alt="" loading="lazy" />{!blocked && <span className="addb">+</span>}</span>
                      ) : (
                        !blocked && <span className="addb solo">+</span>
                      )}
                    </button>
                  );
                })}
              </section>
            ))}
          </main>

          <aside className="rail">
            <div className="order" data-section="rail">
              <h3>Your order</h3>
              {(!cart || cart.lines.length === 0) && <div className="empty">Nothing yet — the menu awaits.</div>}
              {cart?.lines.map((l) => (
                <div className="li" key={l.lineId} data-line={l.lineId}>
                  <span className="q">{l.wire.quantity}×</span>
                  <span style={{ minWidth: 0 }}>
                    {l.name}
                    {l.configSummary && <span className="cfg">{l.configSummary}</span>}
                    <span className="cfg">
                      <button className="linky" aria-label={`One fewer ${l.name}`} onClick={() => setQuantity(storeKey, l.lineId, l.wire.quantity - 1)}>− one</button>
                      {' · '}
                      <button className="linky" aria-label={`One more ${l.name}`} onClick={() => setQuantity(storeKey, l.lineId, l.wire.quantity + 1)}>+ one</button>
                    </span>
                  </span>
                  <span className="amt">{money(l.wire.quantity * l.unitPriceInCents)}</span>
                </div>
              ))}
              {cart && cart.lines.length > 0 && (
                <>
                  <div className="tot"><span>Subtotal</span><span className="money">{money(subtotal)}</span></div>
                  <Link to={`/r/${rid}/s/${sid}/checkout`} className="paybtn" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>Checkout</Link>
                  <div className="finep">Fees & offers are settled at checkout</div>
                </>
              )}
            </div>
          </aside>
        </div>
      </div>

      {count > 0 && (
        <Link to={`/r/${rid}/s/${sid}/checkout`} className="cartbar" aria-label="View cart" data-count={count} style={{ textDecoration: 'none', color: '#fff' }}>
          <span className="n">{count}</span> Checkout
          <span className="amt">{money(subtotal)}</span>
        </Link>
      )}

      {sheet && <ItemSheet key={sheet.itemId} view={sheet} menu={menu} channel={channel} storeKey={storeKey} onClose={() => setSheet(null)} />}
    </>
  );
}
